import {
  hostedUserDocumentsAdd,
  hostedUserDocumentsDelete,
  hostedUserDocumentsGet,
  hostedUserDocumentsList,
  hostedUserDocumentsUpdate,
  partitionHostedUserDocumentsByTrash,
} from '@owlbert/core'
import { generateUploadedDocumentTitle, looksLikeFilename } from '../documents/autoTitleUploadedDocument'
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  TypographyStylesProvider,
  UnstyledButton,
} from '@mantine/core'
import DOMPurify from 'dompurify'
import {
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconEdit,
  IconFileText,
  IconFileTypePdf,
  IconNotes,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import React from 'react'
import { Link } from 'react-router-dom'
import { useOwlbertWebAuth } from '../auth/OwlbertWebAuthContext'
import {
  deleteBrowserHostedUserDocumentFromKb,
  indexBrowserHostedUserDocumentToKb,
  type BrowserHostedUserDocForKb,
} from '../knowledgebase/indexBrowserHostedUserDocument'
import { webLog } from '../logging/webLog'
import {
  createDocument,
  deleteDocument,
  fetchDocuments,
  fetchMe,
  updateDocument,
  uploadDocuments,
  type MeResponse,
  type UserDocumentRow,
} from '../api'
import { WebUserDocumentMarkdownEditor, type WebUserDocumentMarkdownEditorHandle } from '../components/WebUserDocumentMarkdownEditor'
import { OwlbertMarkdown } from '@@web/shared/owlbert_markdown'
import { processBrowserUploadFile } from '../processUploadFile'
import { extractExcelHtml } from '@@web/shared/extract_excel_text'
import { extractWordHtml } from '@@web/shared/extract_word_text'
import { base64ToUint8Array } from '@@web/shared/bytes_base64'
import { isExcelUpload, isWordUpload } from '@@web/shared/upload_policy'
import { searchWorkspaceKnowledgebaseQuery, type KbSearchDocumentHit } from '../chat/runWebChatAutoWithKbTool'

const UPLOAD_MAX_BYTES = 20_000_000

/** Human-readable byte size, e.g. "18.4 MB" / "920 KB". */
function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${String(bytes)} B`
  if (bytes < 1_000_000) return `${(bytes / 1000).toFixed(0)} KB`
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

/** Real uploaded file size when known, otherwise the extracted-text length. */
function docSizeBytes(d: UserDocumentRow): number {
  return typeof d.sizeBytes === 'number' && d.sizeBytes > 0 ? d.sizeBytes : d.content.length
}

function docMatchesClientQuery(d: UserDocumentRow, qLower: string): boolean {
  if (!qLower) return true
  const mime = (d.mimeType ?? '').toLowerCase()
  return (
    d.id.toLowerCase().includes(qLower) ||
    d.name.toLowerCase().includes(qLower) ||
    (d.sourceFilename ?? '').toLowerCase().includes(qLower) ||
    d.contentHash.toLowerCase().includes(qLower) ||
    mime.includes(qLower) ||
    d.content.toLowerCase().includes(qLower)
  )
}

/** Map a hosted row into the table shape used by this page. */
function hostedToUserRow(hosted: {
  id: string
  name: string
  content: string
  mimeType?: string | null
  metadata?: Record<string, unknown>
  blob?: string | null
}): UserDocumentRow {
  const content = hosted.content
  const name = hosted.name?.trim() || 'Untitled'
  const sourceFilename =
    typeof hosted.metadata?.sourceFilename === 'string' ? (hosted.metadata.sourceFilename as string) : null
  const sizeBytes =
    typeof hosted.metadata?.sizeBytes === 'number' ? (hosted.metadata.sizeBytes as number) : null
  const previewHtml =
    typeof hosted.metadata?.previewHtml === 'string' ? (hosted.metadata.previewHtml as string) : null
  const autoTitled = hosted.metadata?.autoTitled === true
  const uploadedAt =
    typeof hosted.metadata?.uploadedAt === 'string' ? (hosted.metadata.uploadedAt as string) : null
  const comment = typeof hosted.metadata?.comment === 'string' ? (hosted.metadata.comment as string) : null
  return {
    id: hosted.id,
    name,
    content,
    contentHash: 'browser',
    chunkCount: Math.max(1, Math.ceil(content.length / 2000)),
    mimeType: hosted.mimeType ?? null,
    sourceFilename,
    sizeBytes,
    blob: hosted.blob ?? null,
    previewHtml,
    autoTitled,
    uploadedAt,
    comment,
  }
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Text size="xs" c="dimmed" style={{ flex: '0 0 84px' }}>
        {label}
      </Text>
      <Text size="xs" style={{ minWidth: 0, wordBreak: 'break-word' }}>
        {value}
      </Text>
    </Group>
  )
}

async function tryIndexHostedLibraryDocToKb(
  bundle: NonNullable<ReturnType<typeof useOwlbertWebAuth>['bundle']>,
  workspaceId: string,
  doc: BrowserHostedUserDocForKb
): Promise<void> {
  try {
    await indexBrowserHostedUserDocumentToKb({
      clients: { embeddings: bundle.client.embeddings, knowledgebase: bundle.client.knowledgebase },
      workspaceId,
      doc,
    })
  } catch (e) {
    webLog('warn', 'kb-index', 'index failed', { error: String(e), id: doc.id })
    notifications.show({
      color: 'yellow',
      title: 'Indexing issue',
      message: `Your document was saved. Search may not include it yet. ${String(e)}`,
    })
  }
}

async function tryDeleteHostedLibraryDocFromKb(
  bundle: NonNullable<ReturnType<typeof useOwlbertWebAuth>['bundle']>,
  workspaceId: string,
  documentId: string
): Promise<void> {
  try {
    await deleteBrowserHostedUserDocumentFromKb({
      clients: { knowledgebase: bundle.client.knowledgebase },
      workspaceId,
      documentId,
    })
  } catch (e) {
    webLog('warn', 'kb-index', 'delete from kb failed', { error: String(e), id: documentId })
  }
}

type KindIcon = React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>

function isPdfWithBlob(d: UserDocumentRow | null): boolean {
  if (!d) return false
  const mime = (d.mimeType ?? '').toLowerCase()
  return mime === 'application/pdf' && Boolean(d.blob?.trim())
}

function hasPreviewHtml(d: UserDocumentRow | null): boolean {
  return Boolean(d?.previewHtml && d.previewHtml.trim())
}

/** Render trusted-but-sanitized HTML (Word/Excel previews) with default typography styling. */
function DocumentHtmlPreview({ html }: { html: string }): React.ReactElement {
  const clean = React.useMemo(
    () =>
      DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        // Keep table structure attributes so merged cells & styling survive.
        ADD_ATTR: ['colspan', 'rowspan', 'class', 'style'],
      }),
    [html],
  )
  return (
    <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
      <TypographyStylesProvider>
        <div className="owlbert-doc-html" dangerouslySetInnerHTML={{ __html: clean }} />
      </TypographyStylesProvider>
    </ScrollArea>
  )
}

/** Which office type to render on-the-fly, when original bytes (blob) are available. */
function officePreviewKind(d: UserDocumentRow | null): 'excel' | 'word' | null {
  if (!d || !d.blob?.trim()) return null
  const name = d.name ?? ''
  const mime = d.mimeType ?? ''
  if (isExcelUpload(name, mime)) return 'excel'
  if (isWordUpload(name, mime)) return 'word'
  return null
}

/** Decode stored original bytes and render Word/Excel to HTML at view time (not at upload). */
function DocumentOfficePreview({ doc, kind }: { doc: UserDocumentRow; kind: 'excel' | 'word' }): React.ReactElement {
  const [html, setHtml] = React.useState<string | null>(null)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    setHtml(null)
    setFailed(false)
    void (async () => {
      try {
        const bytes = base64ToUint8Array(doc.blob as string)
        const result = kind === 'excel' ? await extractExcelHtml(bytes) : await extractWordHtml(bytes)
        if (cancelled) return
        if (result.ok && result.html.trim()) setHtml(result.html)
        else setFailed(true)
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [doc.id, doc.blob, kind])

  if (failed) {
    return (
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
        <OwlbertMarkdown content={doc.content} />
      </ScrollArea>
    )
  }
  if (html == null) {
    return (
      <Stack align="center" justify="center" gap="xs" style={{ flex: 1 }}>
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          Rendering preview…
        </Text>
      </Stack>
    )
  }
  return <DocumentHtmlPreview html={html} />
}

/** Human-readable labels for popular MIME types (exact match). */
const MIME_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  // Microsoft Office (OOXML)
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
  'application/msword': 'Word Document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
  'application/vnd.ms-excel': 'Excel Spreadsheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  // OpenDocument
  'application/vnd.oasis.opendocument.text': 'OpenDocument Text',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet',
  'application/vnd.oasis.opendocument.presentation': 'OpenDocument Presentation',
  // Apple iWork
  'application/vnd.apple.pages': 'Pages Document',
  'application/vnd.apple.numbers': 'Numbers Spreadsheet',
  'application/vnd.apple.keynote': 'Keynote',
  // Text & data
  'text/markdown': 'Markdown',
  'text/csv': 'CSV',
  'text/html': 'HTML',
  'application/rtf': 'Rich Text',
  'text/rtf': 'Rich Text',
  'application/xml': 'XML',
  'text/xml': 'XML',
  'application/x-yaml': 'YAML',
  'text/yaml': 'YAML',
  // Archives
  'application/zip': 'ZIP Archive',
  'application/x-7z-compressed': '7z Archive',
  'application/x-tar': 'TAR Archive',
  'application/gzip': 'Gzip Archive',
  'application/x-rar-compressed': 'RAR Archive',
  // Images
  'image/png': 'PNG Image',
  'image/jpeg': 'JPEG Image',
  'image/gif': 'GIF Image',
  'image/svg+xml': 'SVG Image',
  'image/webp': 'WebP Image',
  'image/heic': 'HEIC Image',
  // Audio / video
  'audio/mpeg': 'MP3 Audio',
  'audio/wav': 'WAV Audio',
  'video/mp4': 'MP4 Video',
  'video/quicktime': 'QuickTime Video',
}

function kindForDocument(d: UserDocumentRow): { label: string; Icon: KindIcon } {
  const m = (d.mimeType ?? '').toLowerCase()
  if (m === 'application/pdf') return { label: 'PDF', Icon: IconFileTypePdf }
  const exact = MIME_LABELS[m]
  if (exact) return { label: exact, Icon: IconFileText }
  if (m === 'text/markdown') return { label: 'Markdown', Icon: IconFileText }
  if (m.startsWith('text/')) return { label: 'Text', Icon: IconFileText }
  if (m.includes('json')) return { label: 'JSON', Icon: IconFileText }
  if (m.startsWith('image/')) return { label: 'Image', Icon: IconFileText }
  if (m.startsWith('audio/')) return { label: 'Audio', Icon: IconFileText }
  if (m.startsWith('video/')) return { label: 'Video', Icon: IconFileText }
  if (m) return { label: m.split('/').pop() || m, Icon: IconFileText }
  return { label: 'Note', Icon: IconNotes }
}

type SortKey = 'name' | 'size'

function SortGlyph({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <IconChevronDown size={14} style={{ opacity: 0.2 }} aria-hidden />
  return dir === 'asc' ? <IconChevronUp size={14} aria-hidden /> : <IconChevronDown size={14} aria-hidden />
}

export const UserDocumentsPage: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const useBrowserHosted =
    auth.configured && auth.sessionReady && Boolean(auth.workspaceId) && Boolean(auth.bundle?.client)

  const [title, setTitle] = React.useState('')
  const [body, setBody] = React.useState('')
  const [rows, setRows] = React.useState<UserDocumentRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [me, setMe] = React.useState<MeResponse | null>(null)
  const [storage, setStorage] = React.useState<'session' | 'hosted' | undefined>(undefined)
  const [newNoteOpen, setNewNoteOpen] = React.useState(false)
  const [uploadOpen, setUploadOpen] = React.useState(false)
  const [pendingDelete, setPendingDelete] = React.useState<UserDocumentRow | null>(null)

  const [editingInPanel, setEditingInPanel] = React.useState(false)
  const [editId, setEditId] = React.useState<string | null>(null)
  const [editTitle, setEditTitle] = React.useState('')
  const [editBody, setEditBody] = React.useState('')
  const editorRef = React.useRef<WebUserDocumentMarkdownEditorHandle | null>(null)
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState<string | null>(null)

  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  // Guards for the background auto-title sweep (page-load rename of un-titled docs).
  const autoTitleSweepingRef = React.useRef(false)
  const autoTitleSweptIdsRef = React.useRef<Set<string>>(new Set())
  // Lazy content loading: the list renders from lightweight summaries (no per-doc
  // content/blob fetch); full content is fetched on demand when a doc is opened.
  const hydratedDocIdsRef = React.useRef<Set<string>>(new Set())
  const rowsRef = React.useRef<UserDocumentRow[]>([])
  const [uploading, setUploading] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState<{ done: number; total: number } | null>(null)
  const [dropActive, setDropActive] = React.useState(false)

  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [filterQuery, setFilterQuery] = React.useState('')
  const [debouncedFilter, setDebouncedFilter] = React.useState('')
  const [kbHits, setKbHits] = React.useState<KbSearchDocumentHit[]>([])
  const [kbLoading, setKbLoading] = React.useState(false)
  const [kbError, setKbError] = React.useState<string | null>(null)
  const [sortKey, setSortKey] = React.useState<SortKey>('name')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc')

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebouncedFilter(filterQuery), 400)
    return () => window.clearTimeout(id)
  }, [filterQuery])

  React.useEffect(() => {
    const q = debouncedFilter.trim()
    if (!useBrowserHosted || !auth.bundle?.client || !auth.workspaceId) {
      setKbHits([])
      setKbError(null)
      setKbLoading(false)
      return
    }
    if (!q) {
      setKbHits([])
      setKbError(null)
      setKbLoading(false)
      return
    }
    let cancelled = false
    setKbLoading(true)
    setKbError(null)
    void searchWorkspaceKnowledgebaseQuery(
      {
        embeddings: auth.bundle.client.embeddings,
        knowledgebase: auth.bundle.client.knowledgebase,
      },
      auth.workspaceId,
      q,
      30,
    )
      .then((res) => {
        if (cancelled) return
        setKbHits(res.documents)
        setKbError(res.error ?? null)
      })
      .finally(() => {
        if (!cancelled) setKbLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [useBrowserHosted, auth.bundle, auth.workspaceId, debouncedFilter])

  const toggleSort = React.useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDir('asc')
  }, [sortKey])

  const libraryRows = React.useMemo(() => {
    const qLive = filterQuery.trim().toLowerCase()
    let list = [...rows]

    if (qLive) {
      const canKb = useBrowserHosted && Boolean(auth.bundle?.client) && Boolean(auth.workspaceId)
      const debAligned =
        debouncedFilter.trim().toLowerCase() === filterQuery.trim().toLowerCase() && debouncedFilter.trim() !== ''
      const kbIds =
        canKb && !kbError && !kbLoading && kbHits.length > 0 && debAligned
          ? new Set(kbHits.map((h) => h.documentId))
          : null

      if (kbIds) {
        list = list.filter((d) => kbIds.has(d.id) || docMatchesClientQuery(d, qLive))
      } else {
        list = list.filter((d) => docMatchesClientQuery(d, qLive))
      }
    }

    const dir = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) * dir
      return (docSizeBytes(a) - docSizeBytes(b)) * dir
    })
    return list
  }, [
    rows,
    filterQuery,
    debouncedFilter,
    sortKey,
    sortDir,
    useBrowserHosted,
    auth.bundle,
    auth.workspaceId,
    kbHits,
    kbError,
    kbLoading,
  ])

  const selectedDoc = React.useMemo(
    () => (selectedId ? rows.find((r) => r.id === selectedId) ?? null : null),
    [rows, selectedId],
  )

  React.useEffect(() => {
    if (selectedId && !rows.some((r) => r.id === selectedId)) {
      setSelectedId(null)
    }
  }, [rows, selectedId])

  /** Blob URL for PDF iframe (matches desktop user documents). */
  React.useEffect(() => {
    let created: string | null = null
    if (isPdfWithBlob(selectedDoc)) {
      try {
        const base64 = selectedDoc!.blob!.trim()
        const binary = atob(base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        created = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      } catch {
        created = null
      }
    }
    setPdfBlobUrl(created)
    return () => {
      if (created) URL.revokeObjectURL(created)
    }
  }, [selectedDoc?.id, selectedDoc?.blob, selectedDoc?.mimeType])

  const [commentDraft, setCommentDraft] = React.useState('')
  const [savingComment, setSavingComment] = React.useState(false)
  React.useEffect(() => {
    setCommentDraft(selectedDoc?.comment ?? '')
  }, [selectedDoc?.id, selectedDoc?.comment])

  const saveComment = React.useCallback(async () => {
    if (!selectedDoc) return
    if (!(useBrowserHosted && auth.bundle && auth.workspaceId)) {
      notifications.show({ color: 'red', message: 'Comments require a hosted workspace.' })
      return
    }
    setSavingComment(true)
    try {
      const client = auth.bundle.client
      const wid = auth.workspaceId
      const uds = client.owlbertHosted.userDocuments
      const fresh = await hostedUserDocumentsGet(uds, wid, selectedDoc.id)
      if (!fresh) return
      const comment = commentDraft.trim()
      await hostedUserDocumentsUpdate(uds, {
        workspaceId: wid,
        request: {
          id: selectedDoc.id,
          name: fresh.name,
          content: fresh.content,
          metadata: { ...(fresh.metadata ?? {}), comment },
          mimeType: fresh.mimeType,
          blob: fresh.blob,
        },
      })
      setRows((prev) => prev.map((r) => (r.id === selectedDoc.id ? { ...r, comment } : r)))
      notifications.show({ color: 'green', message: 'Comment saved.' })
    } catch (e) {
      notifications.show({ color: 'red', message: `Could not save comment. ${String(e)}` })
    } finally {
      setSavingComment(false)
    }
  }, [selectedDoc, useBrowserHosted, auth, commentDraft])

  /** Leaving the row being edited cancels the draft (same idea as switching documents on desktop). */
  React.useEffect(() => {
    if (!editingInPanel) return
    if (!editId || !selectedId) return
    if (selectedId !== editId) {
      setEditingInPanel(false)
      setEditId(null)
      setEditTitle('')
      setEditBody('')
    }
  }, [selectedId, editingInPanel, editId])

  React.useEffect(() => {
    void fetchMe()
      .then(setMe)
      .catch(() => setMe(null))
  }, [])

  const reload = React.useCallback(async () => {
    setLoading(true)
    try {
      if (useBrowserHosted && auth.bundle && auth.workspaceId) {
        const client = auth.bundle.client
        const wid = auth.workspaceId
        const uds = client.owlbertHosted.userDocuments
        const summaries = await hostedUserDocumentsList(uds, wid)
        const { active } = partitionHostedUserDocumentsByTrash(summaries)
        // Build the list straight from summaries (id/name/mimeType/metadata) — no
        // per-document content or blob fetch. Content is hydrated lazily on open.
        hydratedDocIdsRef.current = new Set()
        setRows(
          active.map((s) =>
            hostedToUserRow({ id: s.id, name: s.name, content: '', mimeType: s.mimeType, metadata: s.metadata }),
          ),
        )
        setStorage('hosted')
        return
      }
      const r = await fetchDocuments()
      setRows(r.documents)
      setStorage(r.storage)
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not load documents',
        message: String(e),
      })
    } finally {
      setLoading(false)
    }
  }, [auth.bundle, auth.workspaceId, useBrowserHosted])

  React.useEffect(() => {
    void reload()
  }, [reload])

  React.useEffect(() => {
    rowsRef.current = rows
  }, [rows])

  /** Fetch a document's full content/blob on demand (browser-hosted), caching by id. */
  const ensureContentLoaded = React.useCallback(
    async (id: string): Promise<UserDocumentRow | null> => {
      const existing = rowsRef.current.find((r) => r.id === id) ?? null
      if (!useBrowserHosted || !auth.bundle || !auth.workspaceId) return existing
      if (hydratedDocIdsRef.current.has(id)) return existing
      try {
        const uds = auth.bundle.client.owlbertHosted.userDocuments
        const full = await hostedUserDocumentsGet(uds, auth.workspaceId, id)
        if (!full) return existing
        hydratedDocIdsRef.current.add(id)
        const row = hostedToUserRow(full)
        setRows((prev) => prev.map((r) => (r.id === id ? row : r)))
        return row
      } catch {
        return existing
      }
    },
    [auth.bundle, auth.workspaceId, useBrowserHosted],
  )

  // Hydrate the selected document's content when it becomes active.
  React.useEffect(() => {
    if (selectedId) void ensureContentLoaded(selectedId)
  }, [selectedId, ensureContentLoaded])

  // Background sweep: auto-title any hosted docs that still have a filename-ish
  // name and haven't been auto-titled yet (e.g. uploaded before auto-titling, or
  // whose upload-time rename never completed). Runs once per doc per session and
  // persists an `autoTitled` flag so it never re-attempts the same document.
  React.useEffect(() => {
    if (!useBrowserHosted || !auth.bundle || !auth.workspaceId) return
    const llm = auth.bundle.client.llm
    if (!llm) return
    if (autoTitleSweepingRef.current) return
    const candidates = rows.filter(
      (r) => !r.autoTitled && !autoTitleSweptIdsRef.current.has(r.id) && looksLikeFilename(r.name)
    )
    if (candidates.length === 0) return

    const wid = auth.workspaceId
    const uds = auth.bundle.client.owlbertHosted.userDocuments
    autoTitleSweepingRef.current = true
    void (async () => {
      try {
        for (const cand of candidates) {
          autoTitleSweptIdsRef.current.add(cand.id)
          // Re-fetch the full doc so we preserve all metadata fields on update.
          const fresh = await hostedUserDocumentsGet(uds, wid, cand.id)
          if (!fresh) continue
          const filename = cand.sourceFilename ?? cand.name
          const title = await generateUploadedDocumentTitle({
            llm,
            filename,
            content: fresh.content,
            mimeType: fresh.mimeType ?? cand.mimeType,
          })
          const nextName = title && title !== fresh.name ? title : fresh.name
          try {
            await hostedUserDocumentsUpdate(uds, {
              workspaceId: wid,
              request: {
                id: cand.id,
                name: nextName,
                content: fresh.content,
                // Persist the flag even when no better title was produced, so we
                // never burn another LLM call on this document.
                metadata: { ...(fresh.metadata ?? {}), autoTitled: true },
                mimeType: fresh.mimeType ?? cand.mimeType,
                blob: fresh.blob ?? cand.blob,
              },
            })
            // Patch just this row in place so the list updates without a full
            // reload (which would flash/re-fetch the whole page).
            setRows((prev) =>
              prev.map((r) =>
                r.id === cand.id ? { ...r, name: nextName, autoTitled: true } : r
              )
            )
          } catch {
            // Best-effort; leave the name as-is and retry on a future session.
          }
        }
      } finally {
        autoTitleSweepingRef.current = false
      }
    })()
  }, [rows, useBrowserHosted, auth.bundle, auth.workspaceId, reload])

  const formatUploadError = (code: string): string => {
    switch (code) {
      case 'unsupported_binary_type':
        return 'Binary or media type not supported for text upload.'
      case 'unsupported_file_type':
        return 'File type not allowed. Use plain text, Markdown, code, JSON, CSV, etc.'
      case 'invalid_utf8_or_binary':
        return 'File is not valid UTF-8 text.'
      case 'read_failed':
        return 'Could not read file.'
      case 'pdf_extract_failed':
        return 'Could not extract text from this PDF.'
      case 'excel_extract_failed':
        return 'Could not extract text from this spreadsheet.'
      case 'word_extract_failed':
        return 'Could not extract text from this Word document.'
      case 'file_too_large':
        return `File is too large — the maximum upload size is ${formatBytes(UPLOAD_MAX_BYTES)} per file.`
      default:
        return code
    }
  }

  const handleUploadFiles = async (files: File[]) => {
    if (!files.length) return
    if (useBrowserHosted && !auth.workspaceId) {
      notifications.show({ color: 'yellow', message: 'Choose a workspace in Settings before uploading.' })
      return
    }

    // Pre-flight size check so oversized files are reported up front (with their
    // actual size) instead of failing silently mid-upload.
    const oversize = files.filter((f) => f.size > UPLOAD_MAX_BYTES)
    const withinLimit = files.filter((f) => f.size <= UPLOAD_MAX_BYTES)
    if (oversize.length) {
      notifications.show({
        color: 'red',
        title: oversize.length === 1 ? 'File too large' : `${String(oversize.length)} files too large`,
        message: oversize
          .map((f) => `${f.name} (${formatBytes(f.size)}) exceeds the ${formatBytes(UPLOAD_MAX_BYTES)} limit.`)
          .join('\n'),
      })
    }
    if (!withinLimit.length) return

    setUploading(true)
    setUploadProgress({ done: 0, total: withinLimit.length })
    const failed: { name: string; error: string }[] = []
    let added = 0

    try {
      if (useBrowserHosted && auth.bundle && auth.workspaceId) {
        const client = auth.bundle.client
        const wid = auth.workspaceId
        const uds = client.owlbertHosted.userDocuments

        // Docs uploaded this batch, captured so we can auto-title them in the
        // background once they're persisted.
        const titleCandidates: {
          id: string
          name: string
          content: string
          metadata: Record<string, unknown>
          mimeType: string | null
          blob: string | null
          filename: string
        }[] = []

        for (const file of withinLimit) {
          const parsed = await processBrowserUploadFile(file)
          if ('error' in parsed) {
            failed.push({ name: parsed.originalName, error: parsed.error })
            continue
          }
          const id = crypto.randomUUID()
          const name = parsed.title.trim() || 'Untitled'
          const content = parsed.content.trim() || ' '
          const metadata: Record<string, unknown> = {
            sourceFilename: parsed.originalName,
            sizeBytes: file.size,
            uploadedAt: new Date().toISOString(),
          }
          const blob = typeof parsed.blob === 'string' && parsed.blob.length > 0 ? parsed.blob : null
          await hostedUserDocumentsAdd(uds, {
            workspaceId: wid,
            request: { id, name, content, metadata, mimeType: parsed.mimeType, blob },
          })
          titleCandidates.push({ id, name, content, metadata, mimeType: parsed.mimeType, blob, filename: parsed.originalName })
          await tryIndexHostedLibraryDocToKb(auth.bundle, wid, {
            id,
            name,
            content,
            metadata: { sourceFilename: parsed.originalName },
            mimeType: parsed.mimeType,
          })
          added += 1
          setUploadProgress({ done: added, total: withinLimit.length })
        }
        setStorage('hosted')

        // Background: replace filename-ish names (e.g. "grn_rst_sci_doc.pdf") with
        // concise, content-aware titles. Non-blocking; refreshes the list when done.
        if (titleCandidates.length && auth.bundle?.client.llm) {
          const llm = auth.bundle.client.llm
          void (async () => {
            for (const cand of titleCandidates) {
              if (!looksLikeFilename(cand.name)) continue
              const title = await generateUploadedDocumentTitle({
                llm,
                filename: cand.filename,
                content: cand.content,
                mimeType: cand.mimeType,
              })
              if (!title || title === cand.name) continue
              try {
                await hostedUserDocumentsUpdate(uds, {
                  workspaceId: wid,
                  request: {
                    id: cand.id,
                    name: title,
                    content: cand.content,
                    metadata: { ...cand.metadata, autoTitled: true },
                    mimeType: cand.mimeType,
                    blob: cand.blob,
                  },
                })
                // Patch just this row in place instead of reloading the whole
                // list, so the new title appears without a page flash.
                setRows((prev) =>
                  prev.map((r) =>
                    r.id === cand.id ? { ...r, name: title, autoTitled: true } : r
                  )
                )
              } catch {
                // Best-effort; keep the original name on failure.
              }
            }
          })()
        }
      } else {
        const out = await uploadDocuments(withinLimit)
        setStorage(out.storage)
        added = out.documents.length
        setUploadProgress({ done: added, total: withinLimit.length })
        failed.push(...out.failed)
      }

      if (added > 0) {
        notifications.show({
          color: 'green',
          message: added === 1 ? 'Uploaded 1 document.' : `Uploaded ${String(added)} documents.`,
        })
      }
      if (failed.length) {
        notifications.show({
          color: 'red',
          title: 'Some uploads failed',
          message: failed.map((f) => `${f.name}: ${formatUploadError(f.error)}`).join('\n'),
        })
      }

      await reload()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Upload failed', message: String(e) })
    } finally {
      setUploading(false)
      setUploadProgress(null)
      setDropActive(false)
      setUploadOpen(false)
    }
  }

  const startPanelEdit = async (d: UserDocumentRow) => {
    setSelectedId(d.id)
    const loaded = (await ensureContentLoaded(d.id)) ?? d
    setEditId(d.id)
    setEditTitle(loaded.name)
    setEditBody(loaded.content)
    setEditingInPanel(true)
  }

  const cancelPanelEdit = () => {
    setEditingInPanel(false)
    setEditId(null)
    setEditTitle('')
    setEditBody('')
  }

  const onSaveNew = async () => {
    if (!title.trim() || !body.trim()) {
      notifications.show({ color: 'yellow', message: 'Title and body are required.' })
      return
    }
    try {
      if (useBrowserHosted && auth.bundle && auth.workspaceId) {
        const client = auth.bundle.client
        const wid = auth.workspaceId
        const uds = client.owlbertHosted.userDocuments
        const id = crypto.randomUUID()
        const name = title.trim() || 'Untitled'
        const content = body.trim()
        await hostedUserDocumentsAdd(uds, {
          workspaceId: wid,
          request: { id, name, content },
        })
        await tryIndexHostedLibraryDocToKb(auth.bundle, wid, { id, name, content })
        setStorage('hosted')
        setTitle('')
        setBody('')
        setNewNoteOpen(false)
        notifications.show({ color: 'green', message: 'Saved note.' })
        await reload()
        return
      }
      const out = await createDocument({ name: title.trim(), content: body.trim() })
      setStorage(out.storage)
      setTitle('')
      setBody('')
      setNewNoteOpen(false)
      notifications.show({ color: 'green', message: 'Saved note.' })
      await reload()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: String(e) })
    }
  }

  const onSaveEdit = async () => {
    if (!editId) return
    const bodyMarkdown = (editingInPanel ? editorRef.current?.getMarkdown() : editBody.trim()) ?? ''
    if (!editTitle.trim() || !bodyMarkdown.trim()) {
      notifications.show({ color: 'yellow', message: 'Title and body are required.' })
      return
    }
    try {
      if (useBrowserHosted && auth.bundle && auth.workspaceId) {
        const client = auth.bundle.client
        const wid = auth.workspaceId
        const uds = client.owlbertHosted.userDocuments
        const existing = await hostedUserDocumentsGet(uds, wid, editId)
        if (!existing) throw new Error('Document not found')
        const name = editTitle.trim() || 'Untitled'
        const content = bodyMarkdown.trim()
        await hostedUserDocumentsUpdate(uds, {
          workspaceId: wid,
          request: {
            id: editId,
            name,
            content,
            metadata: existing.metadata ?? {},
            mimeType: existing.mimeType ?? null,
            blob: existing.blob ?? null,
          },
        })
        await tryIndexHostedLibraryDocToKb(auth.bundle, wid, {
          id: editId,
          name,
          content,
          metadata: existing.metadata ?? {},
          mimeType: existing.mimeType ?? null,
        })
        setStorage('hosted')
        notifications.show({ color: 'green', message: 'Updated note.' })
        cancelPanelEdit()
        await reload()
        return
      }
      const out = await updateDocument(editId, { name: editTitle.trim(), content: bodyMarkdown.trim() })
      setStorage(out.storage)
      notifications.show({ color: 'green', message: 'Updated note.' })
      cancelPanelEdit()
      await reload()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Update failed', message: String(e) })
    }
  }

  const performDelete = async (d: UserDocumentRow) => {
    try {
      if (useBrowserHosted && auth.bundle && auth.workspaceId) {
        const client = auth.bundle.client
        const wid = auth.workspaceId
        const uds = client.owlbertHosted.userDocuments
        await tryDeleteHostedLibraryDocFromKb(auth.bundle, wid, d.id)
        await hostedUserDocumentsDelete(uds, { workspaceId: wid, id: d.id })
        setStorage('hosted')
        notifications.show({ color: 'green', message: 'Deleted.' })
        await reload()
        return
      }
      const out = await deleteDocument(d.id)
      setStorage(out.storage)
      notifications.show({ color: 'green', message: 'Deleted.' })
      await reload()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Delete failed', message: String(e) })
    }
  }

  return (
    <Box className="owlbert-web-page">
      <Box className="owlbert-web-page-header">
        <Group justify="space-between" w="100%">
          <Title order={3}>Documents</Title>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPlus size={16} />}
              onClick={() => setNewNoteOpen(true)}
              disabled={useBrowserHosted && !auth.workspaceId}
            >
              New note
            </Button>
            <Button
              className="accent-button"
              leftSection={<IconUpload size={16} />}
              onClick={() => setUploadOpen(true)}
              disabled={useBrowserHosted && !auth.workspaceId}
            >
              Upload
            </Button>
          </Group>
        </Group>
      </Box>

      <Box p="lg" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          {auth.configured && !auth.sessionReady ? (
            <Alert color="blue" title="Sign in to sync">
              Open{' '}
              <Anchor component={Link} to="/settings">
                Settings
              </Anchor>{' '}
              to sign in and choose a workspace.
            </Alert>
          ) : null}
          {me?.hostedBackendConfigured && me.hostedReady === false && me.hostedError ? (
            <Alert color="red" title="Can't reach the server">
              {me.hostedError.message} ({me.hostedError.code})
            </Alert>
          ) : null}
          <Modal opened={newNoteOpen} onClose={() => setNewNoteOpen(false)} title="New note" size="lg" centered>
            <Stack gap="sm">
              <TextInput label="Title" placeholder="Meeting notes" value={title} onChange={(e) => setTitle(e.currentTarget.value)} data-autofocus />
              <Textarea label="Body" placeholder="Write something…" minRows={6} value={body} onChange={(e) => setBody(e.currentTarget.value)} />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setNewNoteOpen(false)}>
                  Cancel
                </Button>
                <Button className="accent-button" onClick={() => void onSaveNew()} disabled={useBrowserHosted && !auth.workspaceId}>
                  Save note
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload documents" size="lg" centered>
          <Paper
            withBorder
            p="md"
            radius="md"
            style={{
              borderStyle: 'dashed',
              ...(dropActive ? { borderColor: 'var(--mantine-color-violet-filled)' } : {}),
            }}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDropActive(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDropActive(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDropActive(false)
              const list = [...e.dataTransfer.files]
              void handleUploadFiles(list)
            }}
          >
            <Stack gap="sm" align={dropActive ? 'center' : undefined}>
              <Text fw={600}>{dropActive ? 'Drop files to upload' : 'Upload documents'}</Text>
              <Text size="sm" c="dimmed">
                Drag files here or choose files. Supported: PDF (text extraction + in-browser PDF preview when stored in your workspace), Markdown, plain text, JSON, CSV, and common UTF-8 source formats. Other binaries (images, archives, etc.) are not supported.
              </Text>
              <Text size="xs" c="dimmed">
                Maximum file size: {formatBytes(UPLOAD_MAX_BYTES)} per file.
              </Text>
              <Group>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.md,.markdown,.txt,.doc,.docx,.xlsx,.xls,.json,.csv,.ts,.tsx,.js,.jsx,.yaml,.yml,.xml,text/*,application/json,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const picked = e.currentTarget.files
                    if (picked?.length) void handleUploadFiles([...picked])
                    e.currentTarget.value = ''
                  }}
                />
                <Button
                  variant="light"
                  leftSection={<IconUpload size={18} />}
                  loading={uploading}
                  disabled={(useBrowserHosted && !auth.workspaceId) || uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose files
                </Button>
              </Group>

              {uploading ? (
                <Stack gap={4}>
                  <Progress
                    value={uploadProgress && uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 100}
                    striped
                    animated
                    aria-label="Upload progress"
                  />
                  <Text size="xs" c="dimmed">
                    {uploadProgress
                      ? `Uploading ${String(uploadProgress.done)} of ${String(uploadProgress.total)}…`
                      : 'Uploading…'}
                  </Text>
                </Stack>
              ) : null}
            </Stack>
          </Paper>
          </Modal>

          <Paper
            withBorder
            p={0}
            radius="md"
            className="owlbert-web-doc-library"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
              <Group
                justify="flex-start"
                p="sm"
                px="md"
                wrap="wrap"
                gap="sm"
                style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
              >
                <TextInput
                  aria-label="Search documents"
                  placeholder="Search titles and content…"
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.currentTarget.value)}
                  size="xs"
                  leftSection={<IconSearch size={16} />}
                  style={{ minWidth: 220, maxWidth: 440, flex: '1 1 220px' }}
                />
              </Group>
              {useBrowserHosted && kbError ? (
                <Alert color="yellow" variant="light" px="md" py="xs" title="Search unavailable">
                  {kbError} Showing text matches only.
                </Alert>
              ) : null}

              {loading ? (
                <Text p="md" c="dimmed">
                  Loading…
                </Text>
              ) : rows.length === 0 ? (
                <Stack p="xl" align="center" gap="sm">
                  <IconFileText size={40} style={{ opacity: 0.4 }} />
                  <Text c="dimmed">No documents yet</Text>
                  <Group gap="xs">
                    <Button
                      variant="default"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => setNewNoteOpen(true)}
                      disabled={useBrowserHosted && !auth.workspaceId}
                    >
                      New note
                    </Button>
                    <Button
                      className="accent-button"
                      leftSection={<IconUpload size={16} />}
                      onClick={() => setUploadOpen(true)}
                      disabled={useBrowserHosted && !auth.workspaceId}
                    >
                      Upload
                    </Button>
                  </Group>
                </Stack>
              ) : libraryRows.length === 0 ? (
                <Stack p="md" gap="xs">
                  <Text c="dimmed">
                    No library rows match “{filterQuery.trim()}”. Clear the search box to see all {String(rows.length)}{' '}
                    item{rows.length === 1 ? '' : 's'}.
                  </Text>
                  {useBrowserHosted &&
                  kbHits.length > 0 &&
                  debouncedFilter.trim().toLowerCase() === filterQuery.trim().toLowerCase() ? (
                    <Text size="sm" c="dimmed">
                      No documents matched that query in your list.
                    </Text>
                  ) : null}
                </Stack>
              ) : (
                <Flex direction={{ base: 'column', lg: 'row' }} align="stretch" gap={0} style={{ flex: 1, minHeight: 0 }}>
                  <Box
                    style={{
                      flex: selectedDoc ? '0 0 280px' : '1 1 52%',
                      minWidth: 0,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'flex-basis 150ms ease',
                    }}
                  >
                    <Table.ScrollContainer minWidth={selectedDoc ? 0 : 520} style={{ flex: 1, minHeight: 0 }}>
                      <Table stickyHeader highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>
                              <UnstyledButton
                                onClick={() => toggleSort('name')}
                                style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              >
                                Name
                                <SortGlyph active={sortKey === 'name'} dir={sortDir} />
                              </UnstyledButton>
                            </Table.Th>
                            {selectedDoc ? null : (
                              <>
                                <Table.Th style={{ width: 60, whiteSpace: 'nowrap' }}>Kind</Table.Th>
                                <Table.Th style={{ width: 56, whiteSpace: 'nowrap' }}>
                                  <UnstyledButton
                                    onClick={() => toggleSort('size')}
                                    style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                  >
                                    Size
                                    <SortGlyph active={sortKey === 'size'} dir={sortDir} />
                                  </UnstyledButton>
                                </Table.Th>
                                <Table.Th style={{ width: 64, whiteSpace: 'nowrap' }} ta="right">
                                  Actions
                                </Table.Th>
                              </>
                            )}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {libraryRows.map((d) => {
                            const { label, Icon } = kindForDocument(d)
                            const selected = d.id === selectedId
                            return (
                              <Table.Tr
                                key={d.id}
                                tabIndex={0}
                                onClick={() => setSelectedId(d.id)}
                                onDoubleClick={() => startPanelEdit(d)}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: selected ? 'var(--mantine-color-violet-light)' : undefined,
                                }}
                              >
                                <Table.Td>
                                  <Group gap="xs" wrap="nowrap">
                                    <Icon size={18} style={{ flexShrink: 0, opacity: 0.9 }} />
                                    <Box style={{ minWidth: 0 }}>
                                      <Text size="sm" fw={600} lineClamp={1}>
                                        {d.name}
                                      </Text>
                                      {d.sourceFilename ? (
                                        <Text size="xs" c="dimmed" lineClamp={1}>
                                          {d.sourceFilename}
                                        </Text>
                                      ) : null}
                                    </Box>
                                  </Group>
                                </Table.Td>
                                {selectedDoc ? null : (
                                  <>
                                    <Table.Td>
                                      <Text size="xs">{label}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text size="xs" ff="monospace">
                                        {formatBytes(docSizeBytes(d))}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td ta="right">
                                      <Group gap={4} justify="flex-end" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                                        <ActionIcon variant="subtle" color="gray" aria-label="Edit" onClick={() => startPanelEdit(d)}>
                                          <IconEdit size={18} />
                                        </ActionIcon>
                                        <ActionIcon variant="subtle" color="red" aria-label="Delete" onClick={() => setPendingDelete(d)}>
                                          <IconTrash size={18} />
                                        </ActionIcon>
                                      </Group>
                                    </Table.Td>
                                  </>
                                )}
                              </Table.Tr>
                            )
                          })}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </Box>

                  <Box
                    className="owlbert-web-doc-preview"
                    p="md"
                    style={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {selectedDoc ? (
                      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
                        <Box style={{ flexShrink: 0 }}>
                          {editingInPanel ? (
                            <TextInput label="Title" value={editTitle} onChange={(e) => setEditTitle(e.currentTarget.value)} />
                          ) : (
                            <Text fw={700} lineClamp={2}>
                              {selectedDoc.name}
                            </Text>
                          )}
                          {(selectedDoc.mimeType ?? '').toLowerCase() === 'application/pdf' && !selectedDoc.blob?.trim() ? (
                            <Alert color="yellow" variant="light" mt="xs" title="PDF preview limited">
                              Original PDF bytes are not stored for this document. Showing extracted text only. Re-upload the PDF to
                              enable full-page preview in the browser.
                            </Alert>
                          ) : null}
                          {editingInPanel ? (
                            <Group gap="xs" mt="sm">
                              <Button size="xs" variant="default" leftSection={<IconX size={14} />} onClick={cancelPanelEdit}>
                                Cancel
                              </Button>
                              <Button
                                size="xs"
                                leftSection={<IconDeviceFloppy size={14} />}
                                className="accent-button"
                                onClick={() => void onSaveEdit()}
                              >
                                Save
                              </Button>
                            </Group>
                          ) : null}
                        </Box>
                        <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          {editingInPanel ? (
                            <WebUserDocumentMarkdownEditor
                              ref={editorRef}
                              key={`${editId ?? selectedDoc.id}-edit`}
                              initialMarkdown={editBody}
                              readOnly={false}
                            />
                          ) : isPdfWithBlob(selectedDoc) && pdfBlobUrl ? (
                            <Stack gap={6} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                              <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: 'var(--mantine-color-body)' }}>
                                <iframe
                                  title={selectedDoc.name || 'PDF'}
                                  src={pdfBlobUrl}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    minHeight: 360,
                                    border: 'none',
                                    display: 'block',
                                  }}
                                />
                              </Box>
                              <Text size="xs" c="dimmed">
                                This preview is read-only. Use the Edit button to change the title or edit extracted text in the
                                rich editor.
                              </Text>
                            </Stack>
                          ) : officePreviewKind(selectedDoc) ? (
                            <DocumentOfficePreview
                              doc={selectedDoc}
                              kind={officePreviewKind(selectedDoc) as 'excel' | 'word'}
                            />
                          ) : hasPreviewHtml(selectedDoc) ? (
                            <DocumentHtmlPreview html={selectedDoc.previewHtml as string} />
                          ) : (
                            <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
                              <OwlbertMarkdown content={selectedDoc.content} />
                            </ScrollArea>
                          )}
                        </Box>
                      </Stack>
                    ) : (
                      <Stack align="center" justify="center" gap="xs" style={{ flex: 1 }}>
                        <IconFileText size={32} style={{ opacity: 0.4 }} />
                        <Text c="dimmed" size="sm">
                          Select a document to preview
                        </Text>
                      </Stack>
                    )}
                  </Box>

                  {selectedDoc ? (
                    <Box
                      className="owlbert-web-doc-meta"
                      p="md"
                      style={{
                        flex: '0 0 300px',
                        minWidth: 0,
                        minHeight: 0,
                        borderLeft: '1px solid var(--mantine-color-default-border)',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
                        <Stack gap="sm">
                          <Group gap="xs">
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconEdit size={14} />}
                              onClick={() => startPanelEdit(selectedDoc)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => setPendingDelete(selectedDoc)}
                            >
                              Delete
                            </Button>
                          </Group>
                          <Paper withBorder radius="md" p="sm">
                            <Stack gap={8}>
                              <Text fw={600} size="sm">
                                Details
                              </Text>
                              <MetaRow label="Type" value={kindForDocument(selectedDoc).label} />
                              <MetaRow label="Size" value={formatBytes(docSizeBytes(selectedDoc))} />
                              {selectedDoc.sourceFilename ? (
                                <MetaRow label="Source file" value={selectedDoc.sourceFilename} />
                              ) : null}
                              <MetaRow
                                label="Uploaded"
                                value={selectedDoc.uploadedAt ? new Date(selectedDoc.uploadedAt).toLocaleString() : 'Unknown'}
                              />
                              {selectedDoc.mimeType ? <MetaRow label="MIME" value={selectedDoc.mimeType} /> : null}
                            </Stack>
                          </Paper>
                          <Paper withBorder radius="md" p="sm">
                            <Stack gap={8}>
                              <Text fw={600} size="sm">
                                Comments
                              </Text>
                              <Textarea
                                placeholder="Add a note about this document…"
                                autosize
                                minRows={3}
                                value={commentDraft}
                                onChange={(e) => setCommentDraft(e.currentTarget.value)}
                              />
                              <Group justify="flex-end">
                                <Button
                                  size="xs"
                                  className="accent-button"
                                  loading={savingComment}
                                  disabled={commentDraft === (selectedDoc.comment ?? '')}
                                  onClick={() => void saveComment()}
                                >
                                  Save comment
                                </Button>
                              </Group>
                            </Stack>
                          </Paper>
                        </Stack>
                      </ScrollArea>
                    </Box>
                  ) : null}
                </Flex>
              )}

              <Group
                gap="xs"
                px="md"
                py={6}
                style={{
                  flexShrink: 0,
                  borderTop: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--mantine-color-body)',
                }}
              >
                <Text size="xs" c="dimmed" fw={500}>
                  {loading ? '…' : `${String(rows.length)} item${rows.length === 1 ? '' : 's'}`}
                  {!loading && filterQuery.trim() ? ` · ${String(libraryRows.length)} shown` : ''}
                  {useBrowserHosted && filterQuery.trim() ? (
                    <>
                      {' '}
                      {kbLoading ? (
                        <Loader size={12} type="dots" />
                      ) : kbError ? (
                        <Text span size="xs" c="red">
                          {' '}
                          (search limited)
                        </Text>
                      ) : kbHits.length > 0 &&
                        debouncedFilter.trim().toLowerCase() === filterQuery.trim().toLowerCase() ? (
                        <Text span size="xs" c="dimmed">
                          {' '}
                          · {String(kbHits.length)} match{kbHits.length === 1 ? '' : 'es'}
                        </Text>
                      ) : null}
                    </>
                  ) : null}
                </Text>
              </Group>
            </Stack>
          </Paper>

          <Modal opened={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)} title="Delete document" centered>
            <Stack gap="md">
              <Text size="sm">
                Delete “{pendingDelete?.name ?? ''}”? This cannot be undone.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setPendingDelete(null)}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={() => {
                    const d = pendingDelete
                    setPendingDelete(null)
                    if (d) void performDelete(d)
                  }}
                >
                  Delete
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      </Box>

    </Box>
  )
}
